// @ts-strict-ignore
import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup } from '@angular/forms';
import { LocaleResourceManager } from '@shared/manager/locale-resource.manager';
import { PropertyValueType } from '@project/constants/property-value-type';
import { PropertyNames, PropertyTemplateVO } from '@project/components/article/model/vo/property-template-vo';
import { AdminToolService } from '@admintool/services/admintool.service';
import { PropertyTemplateService } from '../service/property-template.service';
import { CustomValidators } from '@shared/custom-validators';
import { AlertHelper } from '@shared/alert/alert.helper';
import { BsModalRef } from 'ngx-bootstrap/modal';
import { Observable, of } from 'rxjs';
import { PropertyDefTypes } from './property-def-types.enum';
import { ValueChecked } from './value-checked';

@Component({
  standalone: false,
  selector: 'app-add-edit-property-def',
  templateUrl: './add-edit-property-def.component.html',
  styleUrls: ['./add-edit-property-def.component.scss'],
})
export class AddEditPropertyDefComponent implements OnInit {
  @Input() public property: PropertyTemplateVO;
  @Input() public propertiesNames: PropertyNames;
  @Input() public initiallySelectedPropertyType: string;
  @Output() public finish = new EventEmitter<void>();

  public form: FormGroup;
  public PropertyTypes = PropertyDefTypes;
  public PropertyValueTypes = PropertyValueType;
  public valueTypeTouched: boolean = false;
  public valueTypes: ValueChecked[];
  public selectedPropertyType: string;
  public checkedValueTypes: string[] = [];
  public isPropertyInUse: boolean;

  private overwriteExistingPropertyTemplate: boolean = false;

  constructor(
    private fb: FormBuilder,
    private bsModalRef: BsModalRef,
    private alertHelper: AlertHelper,
    private localeResourceManager: LocaleResourceManager,
    private propertyTemplateService: PropertyTemplateService,
    private adminToolService: AdminToolService
  ) {}

  ngOnInit() {
    this.valueTypes = Object.keys(this.PropertyValueTypes)
      .map((valueType: string) => {
        return new ValueChecked(valueType, false);
      })
      .sort((a: ValueChecked, b: ValueChecked) => {
        return a.name.localeCompare(b.name);
      });

    this.form = this.fb.group({
      propertyType: ['', CustomValidators.required],
      propertyName: ['', [CustomValidators.trimRequired, CustomValidators.required]],
      propertyValueTypes: ['', CustomValidators.required],
      propertyDescription: ['', [CustomValidators.maxLength(100)]],
    });

    if (this.property) {
      this.overwriteExistingPropertyTemplate = true;
      this.setValues();
      this.checkIfPropertyInUse().subscribe((response: boolean) => {
        this.isPropertyInUse = response;

        if (this.isPropertyInUse) {
          this.disableControls();
        }
      });
    } else {
      this.setPropertyType();
    }
  }

  public close(): void {
    this.bsModalRef.hide();
  }

  public save(): void {
    this.savePropertyHandler();
  }

  public getInputError(c: AbstractControl): string {
    if (c && c.hasError('messageKey')) {
      return this.localeResourceManager.getValueByKey(c.getError('messageKey'), { 0: c.getError('param') });
    } else {
      return '';
    }
  }

  public resetValidation(): void {
    this.form.controls.propertyName.updateValueAndValidity();
  }

  public getSelectedValueTypes(valueType: ValueChecked): void {
    this.valueTypeTouched = true;
    if (valueType.checked) {
      this.checkedValueTypes.push(valueType.name);
    } else {
      const index = this.checkedValueTypes.indexOf(valueType.name);
      this.checkedValueTypes.splice(index, 1);
    }
    this.checkedValueTypes.sort();
    this.form.controls.propertyValueTypes.setValue(this.checkedValueTypes.length ? this.checkedValueTypes : null);
  }

  private disableControls(): void {
    this.form.controls.propertyType.disable();
    this.form.controls.propertyName.disable();
  }

  private checkIfPropertyInUse(): Observable<boolean> {
    if (this.adminToolService.isPropertyUsedByCurrentlyLoadedArticle(this.selectedPropertyType, this.property.propertyName)) {
      return of(true);
    }
    return this.propertyTemplateService.isPropertyInUse(this.property.propertyType, this.property.propertyName);
  }

  private setValues(): void {
    if (this.property) {
      this.selectedPropertyType = this.property.propertyType;
      this.form.controls.propertyName.setValue(this.property.propertyName);
      this.form.controls.propertyDescription.setValue(this.property.propertyDescription);
      this.setPropertyValueType();
    }
  }

  private setPropertyValueType(): void {
    this.property.propertyValueTypes.sort();
    this.property.propertyValueTypes.forEach((valueType: string) => {
      this.valueTypes.map((type: ValueChecked) => {
        if (type.name === valueType) {
          type.checked = true;
          this.checkedValueTypes.push(type.name);
        }
      });
    });
    this.form.controls.propertyValueTypes.setValue(this.checkedValueTypes.length ? this.checkedValueTypes : null);
  }

  private setPropertyType(): void {
    this.selectedPropertyType = this.initiallySelectedPropertyType;
    this.form.controls.propertyType.disable();
  }

  private checkIfNameAlreadyExist(): boolean {
    const propertyName: string = this.form.controls.propertyName.value;

    if (this.selectedPropertyType === PropertyDefTypes.BEHAVIOR) {
      return this.propertiesNames.behaviorProperties.includes(propertyName.toLowerCase());
    } else if (this.selectedPropertyType === PropertyDefTypes.PLAUSI) {
      return this.propertiesNames.plausiProperties.includes(propertyName.toLowerCase());
    } else {
      return this.propertiesNames.connectionProperties.includes(propertyName.toLowerCase());
    }
  }

  private savePropertyHandler(): void {
    const isPropertyNameChanged: boolean = this.property ? !(this.property.propertyName === this.form.controls.propertyName.value) : false;
    const propertyNameAlreadyExists: boolean = this.checkIfNameAlreadyExist();
    const isPropertyTypeChanged: boolean = this.checkIfPropertyTypeHasBeenChanged();

    if (this.overwriteExistingPropertyTemplate) {
      if ((isPropertyNameChanged && propertyNameAlreadyExists) || (isPropertyTypeChanged && propertyNameAlreadyExists)) {
        this.showAlertPropertyExist();
      } else if (isPropertyNameChanged && !propertyNameAlreadyExists) {
        this.overwritePropertyTemplate();
      } else if (
        this.form.controls.propertyDescription.value !== this.property.propertyDescription ||
        this.checkIfPropertyValueTypesHaveBeenChanged()
      ) {
        this.savePropertyTemplate();
      } else if (isPropertyTypeChanged && !propertyNameAlreadyExists) {
        this.savePropertyTemplate();
      }
    } else {
      if (propertyNameAlreadyExists) {
        this.showAlertPropertyExist();
      } else {
        this.savePropertyTemplate();
      }
    }
  }

  private showAlertPropertyExist(): void {
    this.alertHelper.showError('managePropertiesDef.savePropertyErrorTitle', 'managePropertiesDef.savePropertyNameAlreadyExists');
    this.form.controls.propertyName.setErrors({ notUnique: true });
  }

  public checkIfPropertyValueTypesHaveBeenChanged(): boolean {
    if (this.property) {
      const result: boolean[] = [];
      this.property.propertyValueTypes.sort();
      this.checkedValueTypes.sort();

      if (this.property.propertyValueTypes.length !== this.checkedValueTypes.length) {
        return true;
      } else {
        for (let i = 0; i < this.checkedValueTypes.length; i++) {
          if (this.property.propertyValueTypes[i] !== this.checkedValueTypes[i]) {
            result.push(true);
          } else {
            result.push(false);
          }
        }
      }
      return result.some((value: boolean) => value) ? true : false;
    }
  }

  private checkIfPropertyTypeHasBeenChanged(): boolean {
    if (this.property) {
      return this.property.propertyType !== this.selectedPropertyType;
    } else {
      return false;
    }
  }

  private overwritePropertyTemplate(): void {
    this.propertyTemplateService
      .overwritePropertyTemplate(this.form.getRawValue() as PropertyTemplateVO, this.property.propertyName)
      .subscribe(() => {
        this.finish.emit();
        this.close();
      });
  }

  private savePropertyTemplate(): void {
    this.propertyTemplateService.savePropertyTemplate(this.form.getRawValue() as PropertyTemplateVO).subscribe(() => {
      this.finish.emit();
      this.close();
    });
  }
}
